import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail, Phone } from "lucide-react";
import { CONTACT, SERVICES, FLEETS } from "../../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URGENCY_OPTIONS = [
  { value: "standard", label: "Standard (within 1–2 business days)" },
  { value: "urgent", label: "Urgent (same-day quote requested)" },
];

const AIRCRAFT_OPTIONS = FLEETS.flatMap((f) =>
  f.families.flatMap((fam) => fam.models)
);

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  aircraft_type: "",
  part_number: "",
  urgency: "standard",
  service_type: "",
  message: "",
};

export default function RFQForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/rfq`, form);
      toast.success("RFQ received — our team will reply shortly.");
      setForm(INITIAL);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E5E7EB] rounded-sm px-4 py-3 text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#0B1D3A] focus:ring-1 focus:ring-[#0B1D3A] transition-colors text-[14px]";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#F8F9FA] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Info column */}
          <aside className="lg:col-span-4">
            <p className="nps-eyebrow nps-divider-gold mb-5">Contact &amp; RFQ</p>
            <h2 className="font-display font-bold text-[#0B1D3A] text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight">
              Reach Out to
              <br />
              <span className="text-[#C9A961]">Our Team.</span>
            </h2>
            <p className="mt-5 text-[#4B5563] text-[15px] leading-relaxed">
              Send part numbers, applicable aircraft, condition requirements,
              and required delivery dates. We&apos;ll respond with sourcing
              options and a firm quote.
            </p>

            <ul className="mt-10 space-y-6">
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 bg-[#0B1D3A] flex items-center justify-center rounded-sm flex-shrink-0">
                  <Phone className="w-4 h-4 text-[#C9A961]" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#4B5563] font-semibold">
                    Sales Department
                  </p>
                  <a
                    href={CONTACT.salesPhoneHref}
                    className="block font-display font-semibold text-[#0B1D3A] text-base mt-1 hover:text-[#C9A961]"
                  >
                    {CONTACT.salesPhone}
                  </a>
                </div>
              </li>

              <li className="flex items-start gap-4">
                <span className="w-10 h-10 bg-[#0B1D3A] flex items-center justify-center rounded-sm flex-shrink-0">
                  <Mail className="w-4 h-4 text-[#C9A961]" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#4B5563] font-semibold">
                    Email
                  </p>
                  <a
                    href={`mailto:${CONTACT.salesEmail}`}
                    className="block font-display font-semibold text-[#0B1D3A] text-[15px] mt-1 hover:text-[#C9A961]"
                  >
                    {CONTACT.salesEmail}
                  </a>
                </div>
              </li>
            </ul>
          </aside>

          {/* Form column */}
          <div className="lg:col-span-8">
            <form
              data-testid="rfq-contact-form"
              onSubmit={submit}
              className="bg-white border border-[#E5E7EB] rounded-sm p-7 lg:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *" id="rfq-name">
                  <input
                    id="rfq-name"
                    data-testid="rfq-name-input"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className={inputCls}
                    placeholder="Jane Smith"
                  />
                </Field>
                <Field label="Company *" id="rfq-company">
                  <input
                    id="rfq-company"
                    data-testid="rfq-company-input"
                    type="text"
                    required
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                    className={inputCls}
                    placeholder="Acme Airlines"
                  />
                </Field>
                <Field label="Email *" id="rfq-email">
                  <input
                    id="rfq-email"
                    data-testid="rfq-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className={inputCls}
                    placeholder="jane@airline.com"
                  />
                </Field>
                <Field label="Phone" id="rfq-phone">
                  <input
                    id="rfq-phone"
                    data-testid="rfq-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className={inputCls}
                    placeholder="+1 305 555 0142"
                  />
                </Field>
                <Field label="Aircraft Type" id="rfq-aircraft">
                  <select
                    id="rfq-aircraft"
                    data-testid="rfq-aircraft-select"
                    value={form.aircraft_type}
                    onChange={(e) => set("aircraft_type", e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select aircraft (optional)</option>
                    {FLEETS.map((f) => (
                      <optgroup key={f.manufacturer} label={f.manufacturer}>
                        {f.families.flatMap((fam) =>
                          fam.models.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))
                        )}
                      </optgroup>
                    ))}
                  </select>
                </Field>
                <Field label="Part Number / Reference" id="rfq-part">
                  <input
                    id="rfq-part"
                    data-testid="rfq-part-input"
                    type="text"
                    value={form.part_number}
                    onChange={(e) => set("part_number", e.target.value)}
                    className={inputCls}
                    placeholder="e.g. 335-005-507-0"
                  />
                </Field>
                <Field label="Service Interest" id="rfq-service">
                  <select
                    id="rfq-service"
                    data-testid="rfq-service-select"
                    value={form.service_type}
                    onChange={(e) => set("service_type", e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select service (optional)</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>
                        {s.title}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Urgency" id="rfq-urgency">
                  <select
                    id="rfq-urgency"
                    data-testid="rfq-urgency-select"
                    value={form.urgency}
                    onChange={(e) => set("urgency", e.target.value)}
                    className={inputCls}
                  >
                    {URGENCY_OPTIONS.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Requirements / Message *" id="rfq-message" className="mt-5">
                <textarea
                  id="rfq-message"
                  data-testid="rfq-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className={inputCls + " resize-none"}
                  placeholder="Provide part numbers, condition (NE/OH/SV), quantity, and required delivery."
                />
              </Field>

              {form.urgency === "urgent" && (
                <div
                  data-testid="rfq-urgent-note"
                  className="mt-5 border border-[#C9A961]/40 bg-[#FFFBEB] text-[#7C5E16] px-4 py-3 rounded-sm text-[13px]"
                >
                  Marked as urgent — we&apos;ll prioritize your quote and reply the
                  same business day.
                </div>
              )}

              <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-[12px] text-[#4B5563]">
                  By submitting, you agree to be contacted by {`Noble Power Solutions`}.
                </p>
                <button
                  type="submit"
                  data-testid="rfq-submit-button"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 bg-[#0B1D3A] hover:bg-[#1E3A5F] disabled:opacity-60 text-white px-8 py-3.5 font-semibold tracking-wide rounded-sm transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Submit RFQ <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
            {/* hidden var to keep linter happy */}
            <span className="hidden">{AIRCRAFT_OPTIONS.length}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, children, className = "" }) {
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4B5563] mb-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
